import { animate, style, transition, trigger } from '@angular/animations';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { AccionAgregada, IntervalSelectedList, IntervalsLoaded, IntervalView, MarkView, MergedListItem, PermissionView, RawMark } from '../../interface/interfaces';

export interface DisplayMarkView {
  nId: number | null | undefined;
  dTiempo_Marca: Date;
  originalTime: Date;
  sIcono_Marcaje: string | null;
  sDispositivo: string;
  bEsPendiente: boolean;
  bEsAprobada: boolean | undefined;
  bIs_Extemporanea: boolean;
  nEstado_Solicitud: number | null;
  isEnProceso: boolean;
  displayActionType: 'add' | 'edit' | 'delete' | null;
  originalNInterval: number;
  originalNType_Mark: number;
}

export interface DisplayIntervalView {
  displayIndex: number;
  nInterval: number;
  inicio: DisplayMarkView | null;
  fin: DisplayMarkView | null;
}

export interface ExtensionBadgeView {
  nMinutos: number;
  dInicio: Date;
  dFin: Date;
}

type DynamicMergedItem =
  | { type: 'interval'; data: DisplayIntervalView; index: number }
  | { type: 'permission'; data: PermissionView }
  | { type: 'extension'; data: ExtensionBadgeView };
import { Subject, switchMap, takeUntil, forkJoin, of, catchError } from 'rxjs';
import * as moment from 'moment';
import { AttendanceService } from 'src/app/modules/core/attendance/service/attendance.service';
import { IconComponent } from "src/app/shared/components/icon/icon.component";
import { SpinnerComponent } from "src/app/shared/components/spinner/spinner.component";
import { CommonModule } from '@angular/common';
import { MatMenuModule } from '@angular/material/menu';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { LayoutService } from 'src/app/core/services/layout.service';
import { DataDatailRequest, ExtensionTardinessItem } from 'src/app/modules/core/attendance/interfaces/attendance.interface';
import { ALLOWED_REQUEST_TYPES, APPROVED_STATES, HIDDEN_STATES, MARK_REGULARIZATION_TYPES, PENDING_STATES } from '../../helpers/enums';
import { marcasExistentesFueraDeJornada } from '../new-request-extemp/validators/validators-extemp';

@Component({
  standalone: true,
  selector: 'mg-interval-list',
  templateUrl: './interval-list.component.html',
  styleUrls: ['./interval-list.component.scss'],
  animations: [
    trigger('cardEnter', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(-8px)' }),
        animate('240ms ease-out', style({ opacity: 1, transform: 'translateY(0)' }))
      ])
    ]),
    trigger('markEnter', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(-4px)' }),
        animate('180ms 60ms ease-out', style({ opacity: 1, transform: 'translateY(0)' }))
      ])
    ])
  ],
  imports: [
    IconComponent,
    SpinnerComponent,
    CommonModule,
    MatMenuModule,
    MatButtonModule,
    MatTooltipModule
  ]
})
export class IntervalListComponent implements OnChanges, OnDestroy {
  @Input() fecha: Date | null = null;
  @Input() nId_Colaborador: number | null = null;
  @Input() editMode: boolean = false;
  @Input() accionesAgregadas: AccionAgregada[] = [];
  @Input() lockButtons: boolean = false;
  @Input() allowedActionType: 'add' | 'edit' | 'delete' | null = null;
  @Input() canDeleteMarks: boolean = true;
  @Input() minMarksCount: number = 0;
  @Input() loadAdjacentJornadas: boolean = false;
  @Output() loadingChange = new EventEmitter<boolean>();
  @Output() editarMarca = new EventEmitter<IntervalSelectedList>();
  @Output() intervalsLoaded = new EventEmitter<IntervalsLoaded>();
  @Output() hasPendingRegularization = new EventEmitter<{ hasPending: boolean; nId_Solicitud: number | null }>();
  @Output() cannotDeleteChange = new EventEmitter<boolean>();

  intervals: IntervalView[] = [];
  adjacentIntervals: IntervalView[] = [];
  permissions: PermissionView[] = [];
  mergedList: ({ type: 'interval'; data: IntervalView; index: number } | { type: 'permission'; data: PermissionView })[] = [];
  tardinessBadge: { sTipo_Badge: string; nMinutos: number } | null = null;
  extensionBadges: ExtensionBadgeView[] = [];
  spinner: boolean = false;
  isScreenSM$ = this._layoutService.isScreenSM$;

  private readonly fechaSearch$ = new Subject<Date>();
  private readonly destroy$ = new Subject<void>();

  get showEditButtons(): boolean {
    return this.allowedActionType === null || this.allowedActionType === 'edit';
  }

  get effectiveMarkCount(): number {
    return this.computedIntervals.reduce((acc, i) => acc + (i.inicio ? 1 : 0) + (i.fin ? 1 : 0), 0);
  }

  get cannotDeleteDueToMin(): boolean {
    return this.minMarksCount > 0 && this.effectiveMarkCount <= this.minMarksCount;
  }

  get showDeleteButtons(): boolean {
    if (this.cannotDeleteDueToMin) return false;
    return this.canDeleteMarks && (this.allowedActionType === null || this.allowedActionType === 'delete');
  }

  canForceDelete(mark: DisplayMarkView | null | undefined): boolean {
    if (!mark || !this.fecha || mark.isEnProceso) return false;
    return marcasExistentesFueraDeJornada(this.fecha, this.intervals, this.accionesAgregadas)
      .some(item => item.nInterval === mark.originalNInterval && item.nType_Mark === mark.originalNType_Mark);
  }

  showDelete(mark: DisplayMarkView | null | undefined): boolean {
    return this.showDeleteButtons || this.canForceDelete(mark);
  }

  fueraMessage(mark: DisplayMarkView | null | undefined): string {
    if (!mark || !this.fecha) return '';
    const found = marcasExistentesFueraDeJornada(this.fecha, this.intervals, this.accionesAgregadas)
      .find(item => item.nInterval === mark.originalNInterval && item.nType_Mark === mark.originalNType_Mark);
    if (!found) return '';
    return `La marca de las ${found.hora} pertenece al ${found.fecha} y ya no corresponde a esta jornada. Elimínala y genérala en esa fecha.`;
  }

  get totalMarcaciones(): number {
    return this.intervals.reduce((acc, i) => acc + (i.inicio ? 1 : 0) + (i.fin ? 1 : 0), 0);
  }

  get totalPendientes(): number {
    return this.intervals.reduce((acc, i) => acc + (i.inicio?.bEsPendiente ? 1 : 0) + (i.fin?.bEsPendiente ? 1 : 0), 0);
  }

  get totalAprobadas(): number {
    return this.intervals.reduce((acc, i) => acc + (i.inicio?.bEsAprobada ? 1 : 0) + (i.fin?.bEsAprobada ? 1 : 0), 0);
  }

  getHora(fecha: Date | undefined): string {
    if (!fecha) return '';
    return moment(fecha).format('HH:mm');
  }

  getDeviceTooltip(mark: DisplayMarkView | MarkView | null | undefined): string {
    if (!mark) return '';
    if (mark.bEsPendiente) return 'Solicitud';
    if (mark.sDispositivo === 'Generado desde Solicitud') return 'Solicitud';
    return mark.sDispositivo ?? '';
  }


  constructor(
    private readonly _attendanceService: AttendanceService,
    private readonly _layoutService: LayoutService,
  ) {
    this.initStream();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['fecha'] || changes['nId_Colaborador']) && this.fecha && this.nId_Colaborador) {
      this.fechaSearch$.next(this.fecha);
    }
    if ((changes['accionesAgregadas'] || changes['minMarksCount']) && !this.spinner) {
      this.cannotDeleteChange.emit(this.cannotDeleteDueToMin);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  isInterval(item: MergedListItem): item is { type: 'interval'; data: IntervalView; index: number } {
    return item.type === 'interval';
  }

  isPermission(item: MergedListItem): item is { type: 'permission'; data: PermissionView } {
    return item.type === 'permission';
  }

  tieneAccionTipo(nInterval: number, nType_Mark: number, tipo: 'edit' | 'delete' | 'add'): boolean {
    return this.accionesAgregadas.some(
      a => a.nInterval === nInterval && a.nType_Mark === nType_Mark && a.actionType === tipo
    );
  }

  private emitState(): void {
    this.intervalsLoaded.emit({
      intervals: this.intervals,
      adjacentIntervals: this.adjacentIntervals,
      totalMarcaciones: this.totalMarcaciones,
      totalPendientes: this.totalPendientes,
      totalAprobadas: this.totalAprobadas,
    });
    this.cannotDeleteChange.emit(this.cannotDeleteDueToMin);
  }

  private initStream(): void {
    this.fechaSearch$.pipe(
      switchMap(fecha => {
        this.spinner = true;
        this.loadingChange.emit(true);
        this.intervals = [];
        this.adjacentIntervals = [];
        this.permissions = [];
        this.mergedList = [];
        this.tardinessBadge = null;
        this.extensionBadges = [];
        const fechaStr = moment(fecha).format('YYYY-MM-DD');
        const emptyMarks = of({ data: { aIntervals: [] as RawMark[] } });
        const adjacentMarks = (day: string) => this.loadAdjacentJornadas
          ? this._attendanceService.getListMarkings(day, false, this.nId_Colaborador!).pipe(catchError(() => emptyMarks))
          : emptyMarks;
        return forkJoin({
          marks: this._attendanceService.getListMarkings(fechaStr, false, this.nId_Colaborador!),
          requests: this._attendanceService.getdetailRequest(this.nId_Colaborador!, fechaStr),
          extensionsAndTardiness: this._attendanceService.getExtensionsAndTardiness(this.nId_Colaborador!, fechaStr),
          previousMarks: adjacentMarks(moment(fechaStr).subtract(1, 'day').format('YYYY-MM-DD')),
          nextMarks: adjacentMarks(moment(fechaStr).add(1, 'day').format('YYYY-MM-DD')),
          fechaStr: of(fechaStr)
        });
      }),
      takeUntil(this.destroy$)
    ).subscribe({
      next: ({ marks, requests, extensionsAndTardiness, previousMarks, nextMarks, fechaStr }) => {
        const rawMarks = marks.data?.aIntervals ?? [];
        this.buildIntervals(rawMarks);
        this.adjacentIntervals = [
          ...this.mapMarksToIntervals(previousMarks.data?.aIntervals ?? []),
          ...this.mapMarksToIntervals(nextMarks.data?.aIntervals ?? []),
        ];
        this.buildPermissions(requests.data ?? []);
        this.buildTardinessAndExtensions(extensionsAndTardiness.data ?? [], fechaStr);
        this.checkPendingRegularizations(requests.data ?? [], rawMarks);
        this.buildMergedList();
        this.spinner = false;
        this.loadingChange.emit(false);
        this.emitState();
      },
      error: () => {
        this.intervals = [];
        this.adjacentIntervals = [];
        this.permissions = [];
        this.tardinessBadge = null;
        this.extensionBadges = [];
        this.mergedList = [];
        this.hasPendingRegularization.emit({ hasPending: false, nId_Solicitud: null });
        this.emitState();
        this.spinner = false;
        this.loadingChange.emit(false);
      }
    });
  }

  buildPermissions(rawRequests: DataDatailRequest[]): void {
    this.permissions = rawRequests
      .filter(r =>
        ALLOWED_REQUEST_TYPES.has(r.nTipo_Solicitud) &&
        !HIDDEN_STATES.has(r.nEstado_Solicitud)
      )
      .map(r => ({
        label: r.sTipo_Solicitud ?? r.sDescripcion ?? 'Permiso',
        dInicio: new Date(r.dFecha_Inicio),
        dFin: new Date(r.dFecha_Fin),
        nEstado: r.nEstado_Solicitud,
        nMinutos: r.nCantidad
      }));
  }

  private buildTardinessAndExtensions(rawItems: ExtensionTardinessItem[], fechaStr: string): void {
    const tardinessItem = rawItems.find(i => i.sTipo_Badge === 'Tardanza' || i.sTipo_Badge === 'Tolerancia');
    this.tardinessBadge = tardinessItem
      ? { sTipo_Badge: tardinessItem.sTipo_Badge, nMinutos: tardinessItem.nMinutos }
      : null;

    this.extensionBadges = rawItems
      .filter(i => i.sTipo_Badge === 'Extensión de Ausencia')
      .map(i => ({
        nMinutos: i.nMinutos,
        dInicio: moment(`${fechaStr} ${i.dHora_Inicio}`, 'YYYY-MM-DD HH:mm:ss').toDate(),
        dFin: moment(`${fechaStr} ${i.dHora_Fin}`, 'YYYY-MM-DD HH:mm:ss').toDate()
      }));
  }

  private checkPendingRegularizations(rawRequests: DataDatailRequest[], rawMarks: RawMark[] = []): void {
    const pendingRequest = rawRequests.find(
      r => MARK_REGULARIZATION_TYPES.has(r.nTipo_Solicitud) && PENDING_STATES.has(r.nEstado_Solicitud)
    );
    const hasPendingFromMarks = rawMarks.some(
      m => m.bIs_Extemporanea === 1 && m.nEstado_Solicitud !== null && PENDING_STATES.has(m.nEstado_Solicitud!)
    );
    const hasPending = !!pendingRequest || hasPendingFromMarks;
    this.hasPendingRegularization.emit({ hasPending, nId_Solicitud: pendingRequest?.nId_Solicitud ?? null });
  }

  buildMergedList(): void {
    const intervalItems = this.intervals.map((interval, idx) => ({
      type: 'interval' as const,
      data: interval,
      index: idx,
      sortTime: (interval.inicio?.dTiempo_Marca ?? interval.fin?.dTiempo_Marca)?.getTime() ?? 0
    }));
    const intervalTimes: number[] = [];
    this.intervals.forEach(i => {
      if (i.inicio?.dTiempo_Marca) intervalTimes.push(i.inicio.dTiempo_Marca.getTime());
      if (i.fin?.dTiempo_Marca) intervalTimes.push(i.fin.dTiempo_Marca.getTime());
    });
    const marginMs = 30 * 60 * 1000;
    const permissionItems = this.permissions
      .filter(permission => {
        const permStart = permission.dInicio.getTime();
        const permEnd = permission.dFin.getTime();
        return intervalTimes.some(t => t >= permStart - marginMs && t <= permEnd + marginMs);
      })
      .map(permission => ({
        type: 'permission' as const,
        data: permission,
        index: -1,
        sortTime: permission.dInicio.getTime()
      }));
    this.mergedList = [...intervalItems, ...permissionItems]
      .sort((a, b) => a.sortTime - b.sortTime)
      .map(({ type, data, index }) => ({ type, data, index } as any));
  }

  private buildIntervals(rawMarks: RawMark[]): void {
    this.intervals = this.mapMarksToIntervals(rawMarks);
  }

  private mapMarksToIntervals(rawMarks: RawMark[]): IntervalView[] {
    if (!rawMarks?.length) return [];

    const grouped = rawMarks.reduce(
      (acc: Record<number, { inicio?: RawMark; fin?: RawMark }>, mark) => {
        if (!acc[mark.nInterval]) acc[mark.nInterval] = {};
        if (mark.nType_Mark === 1) acc[mark.nInterval].inicio = mark;
        if (mark.nType_Mark === 2) acc[mark.nInterval].fin = mark;
        return acc;
      }, {}
    );

    return Object.values(grouped)
      .map(group => ({
        nInterval: group.inicio?.nInterval ?? group.fin?.nInterval ?? 0,
        inicio: group.inicio ? this.toMarkView(group.inicio) : null,
        fin: group.fin ? this.toMarkView(group.fin) : null,
      }))
      .sort((a, b) => {
        const fechaA = a.inicio?.dTiempo_Marca ?? a.fin?.dTiempo_Marca;
        const fechaB = b.inicio?.dTiempo_Marca ?? b.fin?.dTiempo_Marca;
        if (!fechaA || !fechaB) return 0;
        return fechaA.getTime() - fechaB.getTime();
      });
  }

  private toMarkView(mark: RawMark): MarkView {
    return {
      nId: mark.nId ?? null,
      dTiempo_Marca: new Date(mark.dDate),
      sIcono_Marcaje: mark.sIcono_Marcaje,
      sDispositivo: this.resolveDispositivo(mark.nMetodo_Marcaje),
      bEsPendiente: PENDING_STATES.has(mark.nEstado_Solicitud!),
      bEsAprobada: APPROVED_STATES.has(mark.nEstado_Solicitud!),
      bIs_Extemporanea: mark.bIs_Extemporanea === 1,
      lat: null,
      lon: null,
      nEstado_Solicitud: mark.nEstado_Solicitud,
    };
  }

  private resolveDispositivo(nMetodo: number): string {
    const map: Record<number, string> = {
      1: 'Sistema Smart (Web)',
      2: 'App',
      3: 'Equipos',
      4: 'Sistema Smart (Móvil)',
      5: 'Biométrico Facial',
      6: 'Biométrico Huella',
      7: 'Contraseña',
      8: 'Lector de tarjeta',
      9: 'Generado desde Solicitud',
    };
    return map[nMetodo] ?? 'Desconocido';
  }

  tieneAccionActiva(nInterval: number, nType_Mark: number): boolean {
    return this.accionesAgregadas.some(
      a => a.nInterval === nInterval && a.nType_Mark === nType_Mark
    );
  }

  isDynamicInterval(item: DynamicMergedItem): item is { type: 'interval'; data: DisplayIntervalView; index: number } {
    return item.type === 'interval';
  }

  getActionLabel(actionType: 'add' | 'edit' | 'delete' | null): string {
    if (actionType === 'add') return 'REGISTRO';
    if (actionType === 'edit') return 'EDICIÓN';
    if (actionType === 'delete') return 'ELIMINACIÓN';
    return '';
  }

  getActionCss(actionType: 'add' | 'edit' | 'delete' | null): string {
    if (actionType === 'add') return 'bg-[#10B981] text-white';
    if (actionType === 'edit') return 'bg-[#7884E6] text-white';
    if (actionType === 'delete') return 'bg-mg-red-dark text-white';
    return '';
  }

  get computedIntervals(): DisplayIntervalView[] {
    const flat: (DisplayMarkView & { sortTime: number })[] = [];

    this.intervals.forEach(interval => {
      const processSlot = (mark: MarkView | null, nType_Mark: number) => {
        if (!mark) return;
        const action = this.accionesAgregadas.find(
          a => a.nInterval === interval.nInterval && a.nType_Mark === nType_Mark
        );
        if (action?.actionType === 'delete') return;
        const isEdit = action?.actionType === 'edit';
        const time = isEdit ? new Date(action!.dDate) : mark.dTiempo_Marca;
        flat.push({
          nId: mark.nId,
          dTiempo_Marca: time,
          originalTime: mark.dTiempo_Marca,
          sIcono_Marcaje: mark.sIcono_Marcaje,
          sDispositivo: mark.sDispositivo,
          bEsPendiente: mark.bEsPendiente,
          bEsAprobada: mark.bEsAprobada,
          bIs_Extemporanea: mark.bIs_Extemporanea,
          nEstado_Solicitud: mark.nEstado_Solicitud,
          isEnProceso: isEdit,
          displayActionType: action?.actionType ?? null,
          originalNInterval: interval.nInterval,
          originalNType_Mark: nType_Mark,
          sortTime: time.getTime(),
        });
      };
      processSlot(interval.inicio, 1);
      processSlot(interval.fin, 2);
    });

    this.accionesAgregadas.filter(a => a.actionType === 'add').forEach(a => {
      const time = new Date(a.dDate);
      flat.push({
        nId: null,
        dTiempo_Marca: time,
        originalTime: time,
        sIcono_Marcaje: 'schedule',
        sDispositivo: '',
        bEsPendiente: false,
        bEsAprobada: false,
        bIs_Extemporanea: false,
        nEstado_Solicitud: null,
        isEnProceso: true,
        displayActionType: 'add',
        originalNInterval: a.nInterval,
        originalNType_Mark: a.nType_Mark,
        sortTime: time.getTime(),
      });
    });

    flat.sort((a, b) => a.sortTime - b.sortTime);

    const result: DisplayIntervalView[] = [];
    for (let i = 0; i < flat.length; i += 2) {
      const inicio = flat[i] ?? null;
      const fin = flat[i + 1] ?? null;
      result.push({
        displayIndex: Math.floor(i / 2),
        nInterval: inicio?.originalNInterval ?? fin?.originalNInterval ?? 0,
        inicio,
        fin,
      });
    }
    return result;
  }

  get deletedMarks(): DisplayMarkView[] {
    const deleted: DisplayMarkView[] = [];
    this.intervals.forEach(interval => {
      const processSlot = (mark: MarkView | null, nType_Mark: number) => {
        if (!mark) return;
        const action = this.accionesAgregadas.find(
          a => a.nInterval === interval.nInterval && a.nType_Mark === nType_Mark && a.actionType === 'delete'
        );
        if (!action) return;
        deleted.push({
          nId: mark.nId,
          dTiempo_Marca: mark.dTiempo_Marca,
          originalTime: mark.dTiempo_Marca,
          sIcono_Marcaje: mark.sIcono_Marcaje,
          sDispositivo: mark.sDispositivo,
          bEsPendiente: mark.bEsPendiente,
          bEsAprobada: mark.bEsAprobada,
          bIs_Extemporanea: mark.bIs_Extemporanea,
          nEstado_Solicitud: mark.nEstado_Solicitud,
          isEnProceso: true,
          displayActionType: 'delete',
          originalNInterval: interval.nInterval,
          originalNType_Mark: nType_Mark,
        });
      };
      processSlot(interval.inicio, 1);
      processSlot(interval.fin, 2);
    });
    return deleted.sort((a, b) => a.dTiempo_Marca.getTime() - b.dTiempo_Marca.getTime());
  }

  get dynamicMergedList(): DynamicMergedItem[] {
    const computed = this.computedIntervals;
    const intervalItems = computed.map(interval => ({
      type: 'interval' as const,
      data: interval,
      index: interval.displayIndex,
      sortTime: (interval.inicio?.dTiempo_Marca ?? interval.fin?.dTiempo_Marca)?.getTime() ?? 0,
    }));

    const intervalTimes: number[] = [];
    computed.forEach(i => {
      if (i.inicio?.dTiempo_Marca) intervalTimes.push(i.inicio.dTiempo_Marca.getTime());
      if (i.fin?.dTiempo_Marca) intervalTimes.push(i.fin.dTiempo_Marca.getTime());
    });
    const marginMs = 30 * 60 * 1000;
    const permissionItems = this.permissions
      .filter(p => {
        const pStart = p.dInicio.getTime();
        const pEnd = p.dFin.getTime();
        return intervalTimes.some(t => t >= pStart - marginMs && t <= pEnd + marginMs);
      })
      .map(p => ({
        type: 'permission' as const,
        data: p,
        index: -1,
        sortTime: p.dInicio.getTime(),
      }));

    const extensionItems = this.extensionBadges.map(e => ({
      type: 'extension' as const,
      data: e,
      index: -1,
      sortTime: e.dInicio.getTime(),
    }));

    return [...intervalItems, ...permissionItems, ...extensionItems]
      .sort((a, b) => a.sortTime - b.sortTime)
      .map(({ type, data, index }) => ({ type, data, index } as DynamicMergedItem));
  }
}